import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useShipperCreditProfile } from "@/hooks/useShipperCreditProfile";
import { CreditProfileSummary } from "@/components/credit-profile/CreditProfileSummary";
import { CompleteCreditProfileModal } from "@/components/credit-profile/CompleteCreditProfileModal";
import { Pencil, CheckCircle2 } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";

interface Props {
  companyId: string;
  canEdit: boolean;
}

export function CompanyCreditProfileTab({ companyId, canEdit }: Props) {
  const { profile, refresh, completion } = useShipperCreditProfile(companyId);
  const [open, setOpen] = useState(false);

  const lastUpdated = profile?.updated_at
    ? formatDistanceToNow(parseISO(profile.updated_at), { addSuffix: true })
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-lg border bg-card p-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {completion.isComplete && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            <p className="text-sm font-medium">
              {completion.isComplete
                ? "Credit profile complete"
                : `${completion.complete} of ${completion.total} required fields complete`}
            </p>
          </div>
          {lastUpdated && (
            <p className="text-xs text-muted-foreground">Last updated {lastUpdated}</p>
          )}
          <div className="mt-2 h-1.5 w-64 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full transition-all ${completion.isComplete ? "bg-emerald-500" : "bg-[#02857E]"}`}
              style={{ width: `${completion.percent}%` }}
            />
          </div>
        </div>
        {canEdit && (
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            className="bg-[#02857E] hover:bg-[#026e68] text-white shadow-sm"
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            {profile ? "Edit Your Credit Profile" : "Create Your Credit Profile"}
          </Button>
        )}

      </div>

      <CreditProfileSummary profile={profile} variant="full" />

      {canEdit && (
        <CompleteCreditProfileModal
          open={open}
          onOpenChange={setOpen}
          companyId={companyId}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

